import ClaimsComparisonSettingsDialog from "./ClaimsComparisonSettingsDialog";
import ClaimsMatcherModal from "./ClaimsMatcherModal";
import ClaimsSlider from "./ClaimsSlider";
import FilteredClaimsChart from "./FilteredClaimsChart";
import ManualClaimsLinkerModal from "./ManualClaimsLinkerModal";
import TooManyClaimsOverlay from "./TooManyClaimsOverlay";
import {
  detectSimpleRenewalMatches,
  buildDifferencesForDuplicateGroup,
  filterUnmatchedComparisons,
  buildClaimIdMap,
} from "./claimsMatching.support";
import { Settings as SettingsIcon } from "@mui/icons-material";
import {
  Alert,
  Button,
  Box,
  Stack,
  Typography,
  Menu,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as chartUtils from "chartUtils";
import * as charts from "common/charts";
import * as dates from "common/dates";
import * as logger from "common/logger";
import { currencyFormat } from "common/numbers";
import Component from "components/Component";
import BigLoader from "components/common/BigLoader";
import IconButton from "components/common/IconButton";
import * as config from "config";
import * as flags from "flags";
import * as _ from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DefaultTooltipContent } from "recharts/lib/component/DefaultTooltipContent";
import { useMatchClaims } from "services/aiAgents";
import * as claimsService from "services/claimsService";
import * as staticDataService from "services/staticDataService";
import * as claimsActions from "store/actions/input/claims/claimsActions";
import useDebouncedValue from "store/hooks/useDebouncedValue";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";
import * as stateSelectors from "store/selectors/stateSelectors";

const tooltipLabelFormatter = (value, props) => {
  return dates.formatDate(props?.[0]?.payload?.date);
};

const makeTooltipPayload = (originalPayload, theme) => {
  const diff = originalPayload?.[0]?.payload;

  if (diff == null) {
    return [];
  }

  const { delta, status, date, prior, current } = diff;

  const statusStyles = {
    new: {
      background: theme.brand.compare.new,
      border: `2px solid ${theme.brand.green[200]}`,
      borderRadius: "4px",
      color: theme.brand.green[300],
    },
    missing: {
      background: theme.brand.compare.missing,
      border: `2px solid ${theme.brand.accent[400]}`,
      borderRadius: "4px",
      color: theme.brand.accent[500],
    },
    different: {
      background: theme.brand.compare.different,
      border: `2px solid ${theme.brand.red[600]}`,
      borderRadius: "4px",
      color: theme.brand.red[600],
    },
  };

  const payload = [
    {
      name: "Date",
      value: <span>{date}</span>,
    },
    {
      name: "Status",
      value: (
        <span style={{ ...statusStyles[status] }}>{status?.toUpperCase()}</span>
      ),
    },
  ];

  if (status === "different") {
    payload.push({
      name: "Delta",
      value: (
        <span
          style={{
            color: delta < 0 ? theme.brand.red[700] : theme.brand.green[300],
          }}
        >
          {currencyFormat(delta)}
        </span>
      ),
    });

    const oldValue = prior?.numericDeltas?.[0];
    payload.push({
      name: "Old Value",
      value: <span>{oldValue != null ? currencyFormat(oldValue) : ""}</span>,
    });

    const newValue = current?.numericDeltas?.[0];
    payload.push({
      name: "New Value",
      value: <span>{newValue != null ? currencyFormat(newValue) : ""}</span>,
    });
  } else {
    payload.push({
      name: "Value",
      value: (
        <span
          style={{
            color: delta < 0 ? theme.brand.red[700] : theme.brand.green[300],
          }}
        >
          {currencyFormat(delta)}
        </span>
      ),
    });
  }

  return payload;
};

const CustomTooltipContent = (props) => {
  const theme = useTheme();
  return (
    <DefaultTooltipContent
      {...props}
      payload={makeTooltipPayload(props?.payload, theme)}
    />
  );
};

const tooltipFormatter = (value) => {
  if (typeof value === "number") {
    return currencyFormat(value);
  }
  return value;
};

const WithLoading = ({ isLoading, children }) =>
  isLoading ? <BigLoader /> : children;

const WithError = ({ error, children }) =>
  error ? <Alert severity={"error"}>{error}</Alert> : children;

const ClaimsAndDeltaChart = ({ query, setQuery }) => {
  const theme = useTheme();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMatcherModalOpen, setIsMatcherModalOpen] = useState(false);
  const [isManualLinkerOpen, setIsManualLinkerOpen] = useState(false);
  const [manualLinkerInitialTab, setManualLinkerInitialTab] = useState(0);
  const [editMenuAnchor, setEditMenuAnchor] = useState(null);
  const [zoom, setZoom] = useState();
  const [
    lastMatchedComparisonsCount,
    setLastMatchedComparisonsCount,
  ] = useState(0);

  const aiClaimsMatchEnabled = staticDataService.useFeatureFlag(
    flags.AI_CLAIMS_MATCH
  );
  const runAiMatchOnLoad = staticDataService.useFeatureFlag(
    flags.RUN_AI_CLAIMS_MATCH_ON_LOAD
  );

  const [matchClaimsApi, { isLoading: aiMatchingLoading }] = useMatchClaims();

  const storedMatchingResults = useSelector(
    (state) => state.input?.claims?.aiMatchingResults
  );
  const storedPriorClaims = useSelector(
    (state) => state.input?.claims?.aiMatchingPriorClaims
  );
  const storedCurrentClaims = useSelector(
    (state) => state.input?.claims?.aiMatchingCurrentClaims
  );

  const [aiMatchingResults, setAiMatchingResults] = useState(
    storedMatchingResults || null
  );
  const [aiMatchingPriorClaims, setAiMatchingPriorClaims] = useState(
    storedPriorClaims || []
  );
  const [aiMatchingCurrentClaims, setAiMatchingCurrentClaims] = useState(
    storedCurrentClaims || []
  );
  const acceptedMatches = useSelector(claimsSelectors.selectAiClaimsMatches);
  const manualMatches = useSelector(claimsSelectors.selectManualClaimsMatches);
  const deltaThreshold = useSelector(
    claimsSelectors.selectDeltaClaimsThreshold
  );
  const dispatch = useDispatch();

  const zoomIdentifier = "ClaimsAndDeltaChart";
  const chartZoom = useSelector((state) => state.temp?.claims?.chartZoom)?.[
    zoomIdentifier
  ];

  const cartesianGridId = useRef("cartesian-grid");

  const priorQuery = useDebouncedValue(
    useSelector((state) =>
      claimsSelectors.selectInputClaims(stateSelectors.selectPriorState(state))
    )
  );
  const currentQuery = useDebouncedValue(
    useSelector(claimsSelectors.selectInputClaims)
  );
  const hasPriorState = !_.isEmpty(
    useSelector(stateSelectors.selectPriorState)
  );

  const { data, isFetching, isError } = claimsService.useClaimsComparisonQuery(
    {
      ...query,
      priorQuery,
      currentQuery,
    },
    {
      skip: !hasPriorState || !priorQuery || !currentQuery,
    }
  );

  const {
    currentData: allClaimsChart,
    isFetching: isAllClaimsChartFetching,
  } = claimsService.useAllClaimsChartQuery({
    claimsQuery: currentQuery,
    claimsAbove: deltaThreshold,
  });

  const {
    currentData: priorAllClaimsChart,
    isFetching: isPriorAllClaimsChartFetching,
  } = claimsService.useAllClaimsChartQuery({
    claimsQuery: priorQuery,
    claimsAbove: deltaThreshold,
  });

  const {
    chartRef,
    tooltipPosition,
  } = chartUtils.useTooltipPositionerFixingBottomRightToChartTopRight();

  const [, biggestClaim] = charts.getNumberDomain(
    priorAllClaimsChart,
    allClaimsChart
  );

  const numberTicks = charts.makeTicksForNumericDomain([0, biggestClaim]);
  const numberDomain = [0, numberTicks.at(-1)];

  let doubleNumberTicks = numberTicks.slice(1).map((x) => -x);
  doubleNumberTicks.reverse();
  doubleNumberTicks = [...doubleNumberTicks, ...numberTicks];
  const doubleNumberDomain = [-biggestClaim, biggestClaim];

  const dateDomain = charts.getDateDomain(priorAllClaimsChart, allClaimsChart);

  const zoomedDomain = useMemo(
    () => (chartZoom ? [chartZoom.from, chartZoom.to] : dateDomain),
    [chartZoom, dateDomain]
  );

  const getMonthOfEvent = (e) => {
    if (e?.chartX == null) {
      return undefined;
    }
    const gridEl = document.getElementById(cartesianGridId);
    if (gridEl == null) {
      return undefined;
    }
    const gridX = e.chartX - gridEl.getAttribute("x");
    const gridWidth = gridEl.getAttribute("width");
    const along = Math.min(Math.max(0, gridX / gridWidth), 1);
    const month = zoomedDomain[0] + along * (zoomedDomain[1] - zoomedDomain[0]);
    return month;
  };

  const matchedClaimPairs = useMemo(() => {
    const matchedPriorIds = new Set();
    const matchedCurrentIds = new Set();

    manualMatches.forEach((match) => {
      match.prior_claim_ids.forEach((priorId) => matchedPriorIds.add(priorId));
      match.current_claim_ids.forEach((currentId) =>
        matchedCurrentIds.add(currentId)
      );
    });

    acceptedMatches.forEach((match) => {
      const hasManualConflict =
        match.prior_claim_ids.some((id) => matchedPriorIds.has(id)) ||
        match.current_claim_ids.some((id) => matchedCurrentIds.has(id));

      if (!hasManualConflict) {
        match.prior_claim_ids.forEach((priorId) =>
          matchedPriorIds.add(priorId)
        );
        match.current_claim_ids.forEach((currentId) =>
          matchedCurrentIds.add(currentId)
        );
      }
    });

    return { matchedPriorIds, matchedCurrentIds };
  }, [manualMatches, acceptedMatches]);

  const createComparisonKey = useCallback(
    (comparison) =>
      `${comparison.date}_${comparison.status}_${
        comparison.prior?.textDeltas?.[1] || "null"
      }_${comparison.current?.textDeltas?.[1] || "null"}`,
    []
  );

  const comparisonToClaimId = useMemo(() => {
    const map = new Map();
    const unmatchedComparisons = filterUnmatchedComparisons(
      data?.comparisons ?? []
    );

    unmatchedComparisons.forEach((comparison) => {
      const compKey = createComparisonKey(comparison);
      const priorId = comparison.prior ? `prior_${compKey}` : null;
      const currentId = comparison.current ? `current_${compKey}` : null;
      map.set(comparison, { priorId, currentId });
    });

    return map;
  }, [data?.comparisons, createComparisonKey]);

  const includedDeltas = useMemo(() => {
    return (data?.comparisons ?? []).filter((comparison) => {
      const delta = comparison.delta ?? 0;
      const prior = comparison.prior?.numericDeltas?.[0] ?? 0;
      const current = comparison.current?.numericDeltas?.[0] ?? 0;

      const meetsThreshold =
        Math.abs(delta) > 0 &&
        (prior > deltaThreshold || current > deltaThreshold) &&
        comparison.months > zoomedDomain[0] &&
        comparison.months < zoomedDomain[1];

      if (!meetsThreshold) return false;

      if (acceptedMatches.length > 0) {
        const pairKey = comparisonToClaimId.get(comparison);
        if (pairKey) {
          const { priorId, currentId } = pairKey;
          const priorMatched =
            priorId && matchedClaimPairs.matchedPriorIds.has(priorId);
          const currentMatched =
            currentId && matchedClaimPairs.matchedCurrentIds.has(currentId);
          if (priorMatched || currentMatched) return false;
        }
      }

      return true;
    });
  }, [
    data?.comparisons,
    deltaThreshold,
    zoomedDomain,
    acceptedMatches,
    matchedClaimPairs,
    comparisonToClaimId,
  ]);

  const createClaimFromComparison = useCallback(
    (comparison, isCurrentClaim = false) => {
      const data = isCurrentClaim ? comparison.current : comparison.prior;
      const prefix = isCurrentClaim ? "current" : "prior";
      const compKey = createComparisonKey(comparison);
      const claimId = `${prefix}_${compKey}`;

      return {
        claim_id: claimId,
        date: comparison.date,
        coverage: data.textDeltas?.[0],
        claimant_name: data.textDeltas?.[1],
        description: data.textDeltas?.[2],
        open_closed: data.textDeltas?.[3],
        total_loss: data.numericDeltas?.[0],
      };
    },
    [createComparisonKey]
  );

  const handleAiMatching = useCallback(async () => {
    dispatch(claimsActions.updateAiClaimsMatches([]));
    dispatch(
      claimsActions.updateAiMatchingData({
        results: null,
        priorClaims: [],
        currentClaims: [],
      })
    );

    if (!data?.comparisons || data.comparisons.length === 0) return;

    const priorClaimsMap = new Map();
    const currentClaimsMap = new Map();
    const unmatchedComparisons = filterUnmatchedComparisons(data.comparisons);

    if (unmatchedComparisons.length === 0) return;

    unmatchedComparisons.forEach((comparison) => {
      if (comparison.prior) {
        const priorClaim = createClaimFromComparison(comparison, false);
        priorClaimsMap.set(priorClaim.claim_id, priorClaim);
      }

      if (comparison.current) {
        const currentClaim = createClaimFromComparison(comparison, true);
        currentClaimsMap.set(currentClaim.claim_id, currentClaim);
      }
    });

    const priorClaimsArray = Array.from(priorClaimsMap.values());
    const currentClaimsArray = Array.from(currentClaimsMap.values());

    if (priorClaimsArray.length === 0 || currentClaimsArray.length === 0)
      return;

    setAiMatchingResults(null);

    try {
      const simpleMatches = detectSimpleRenewalMatches(
        priorClaimsArray,
        currentClaimsArray
      );

      const result = await matchClaimsApi({
        priorClaims: priorClaimsArray,
        currentClaims: currentClaimsArray,
      }).unwrap();

      const aiResults = result?.results || [];
      const takenPrior = new Set();
      const takenCurrent = new Set();
      aiResults.forEach((g) => {
        (g.prior_claim_ids || []).forEach((id) => takenPrior.add(String(id)));
        (g.current_claim_ids || []).forEach((id) =>
          takenCurrent.add(String(id))
        );
      });

      const currentById = buildClaimIdMap(currentClaimsArray);
      const priorById = buildClaimIdMap(priorClaimsArray);
      const enrichedSimple = simpleMatches.map((g) => {
        const priorClaims = (g.prior_claim_ids || [])
          .map((id) => priorById.get(String(id)))
          .filter(Boolean);
        const currentClaims = (g.current_claim_ids || [])
          .map((id) => currentById.get(String(id)))
          .filter(Boolean);
        const allClaims = [...priorClaims, ...currentClaims];
        const {
          field_differences,
          non_conforming_fields,
          overall_reasoning,
        } = buildDifferencesForDuplicateGroup(allClaims);
        return {
          ...g,
          field_differences,
          non_conforming_fields,
          overall_reasoning,
        };
      });

      const filteredSimple = enrichedSimple.filter(
        (g) =>
          (g.prior_claim_ids || []).every(
            (id) => !takenPrior.has(String(id))
          ) &&
          (g.current_claim_ids || []).every(
            (id) => !takenCurrent.has(String(id))
          )
      );

      const mergedResults = {
        ...result,
        results: [...filteredSimple, ...aiResults],
      };

      setAiMatchingPriorClaims(priorClaimsArray);
      setAiMatchingCurrentClaims(currentClaimsArray);
      setAiMatchingResults(mergedResults);
      setIsMatcherModalOpen(true);
    } catch (error) {
      logger.error("AI Claims Matcher Error:", error);
    }
  }, [data, dispatch, createClaimFromComparison, matchClaimsApi]);

  const handleApplyMatches = (accepted) => {
    dispatch(claimsActions.updateAiClaimsMatches(accepted));
    dispatch(
      claimsActions.updateAiMatchingData({
        results: aiMatchingResults,
        priorClaims: aiMatchingPriorClaims,
        currentClaims: aiMatchingCurrentClaims,
      })
    );
    setIsMatcherModalOpen(false);
  };

  const handleEditMatches = () => {
    const resultsToUse = storedMatchingResults || aiMatchingResults;
    const priorToUse = storedPriorClaims || aiMatchingPriorClaims;
    const currentToUse = storedCurrentClaims || aiMatchingCurrentClaims;

    if (resultsToUse && resultsToUse.results?.length > 0) {
      setAiMatchingResults(resultsToUse);
      setAiMatchingPriorClaims(priorToUse);
      setAiMatchingCurrentClaims(currentToUse);
      setIsMatcherModalOpen(true);
    }
  };

  const handleOpenManualLinkerFromBanner = () => {
    setManualLinkerInitialTab(1);
    setIsManualLinkerOpen(true);
  };

  const handleSaveManualMatches = (matches) => {
    dispatch(claimsActions.updateManualClaimsMatches(matches));
  };

  useEffect(() => {
    if (
      aiClaimsMatchEnabled &&
      runAiMatchOnLoad &&
      data?.comparisons?.length > 0 &&
      !isFetching &&
      !aiMatchingLoading
    ) {
      const unmatchedComparisons = data.comparisons.filter((comparison) => {
        const hasDelta = comparison.delta != null && comparison.delta !== 0;
        const isUnmatched = ["different", "new", "missing"].includes(
          comparison.status
        );
        return hasDelta && isUnmatched;
      });

      const currentUnmatchedCount = unmatchedComparisons.length;
      const hasNoResults = !storedMatchingResults && !aiMatchingResults;
      const claimsHaveChanged =
        currentUnmatchedCount !== lastMatchedComparisonsCount;

      if (currentUnmatchedCount > 0 && hasNoResults && claimsHaveChanged) {
        setLastMatchedComparisonsCount(currentUnmatchedCount);
        handleAiMatching();
      }
    }
  }, [
    aiClaimsMatchEnabled,
    runAiMatchOnLoad,
    storedMatchingResults,
    aiMatchingResults,
    data,
    isFetching,
    aiMatchingLoading,
    lastMatchedComparisonsCount,
    handleAiMatching,
  ]);

  if (!hasPriorState) {
    return <></>;
  }

  return (
    <Component
      title={`Claims Delta (For Claims > $${deltaThreshold.toLocaleString(
        "en-US",
        {
          maximumFractionDigits: 0,
        }
      )})`}
      options={
        <Stack direction={"row"} spacing={1}>
          {aiClaimsMatchEnabled && (
            <Button
              variant={"contained"}
              color={"primary"}
              size={"small"}
              onClick={handleAiMatching}
              disabled={aiMatchingLoading}
            >
              {aiMatchingLoading ? "Matching..." : "Match Claims"}
            </Button>
          )}
          <Button
            variant={"contained"}
            color={"primary"}
            size={"small"}
            onClick={() => {
              setManualLinkerInitialTab(0);
              setIsManualLinkerOpen(true);
            }}
          >
            {"Manual Link"}
          </Button>
          <IconButton
            variant={"bright"}
            scale={"small"}
            icon={SettingsIcon}
            tooltip={"Settings"}
            onClick={() => setIsSettingsOpen(true)}
          />
        </Stack>
      }
    >
      <ClaimsComparisonSettingsDialog
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        query={query}
        setQuery={setQuery}
      />
      {aiClaimsMatchEnabled && (
        <ClaimsMatcherModal
          open={isMatcherModalOpen}
          onClose={() => setIsMatcherModalOpen(false)}
          matchResults={aiMatchingResults}
          onApply={handleApplyMatches}
          priorClaims={aiMatchingPriorClaims}
          currentClaims={aiMatchingCurrentClaims}
          initialAcceptedMatches={acceptedMatches}
        />
      )}
      <ManualClaimsLinkerModal
        open={isManualLinkerOpen}
        onClose={() => setIsManualLinkerOpen(false)}
        priorClaims={aiMatchingPriorClaims}
        currentClaims={aiMatchingCurrentClaims}
        existingMatches={manualMatches}
        onSaveMatches={handleSaveManualMatches}
        initialTab={manualLinkerInitialTab}
      />
      <Stack spacing={2}>
        {(manualMatches.length > 0 || acceptedMatches.length > 0) && (
          <>
            <Alert
              severity={"success"}
              action={
                <Button
                  variant={"contained"}
                  color={"secondary"}
                  size={"small"}
                  onClick={(e) => {
                    const hasBothTypes =
                      manualMatches.length > 0 && acceptedMatches.length > 0;
                    if (hasBothTypes) {
                      setEditMenuAnchor(e.currentTarget);
                    } else if (manualMatches.length > 0) {
                      handleOpenManualLinkerFromBanner();
                    } else {
                      handleEditMatches();
                    }
                  }}
                  disableElevation
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                >
                  {"Edit"}
                </Button>
              }
            >
              {acceptedMatches.length > 0 && manualMatches.length > 0 ? (
                <>
                  {`${acceptedMatches.length} claim${
                    acceptedMatches.length === 1 ? "" : "s"
                  } matched with AI. ${manualMatches.length} claim${
                    manualMatches.length === 1 ? "" : "s"
                  } linked manually. Matches are applied for delta calculations between renewal and previous period.`}
                </>
              ) : acceptedMatches.length > 0 ? (
                <>
                  {`${acceptedMatches.length} matched claim${
                    acceptedMatches.length === 1 ? "" : "s"
                  } between renewal and previous period applied for delta calculations.`}
                </>
              ) : (
                <>
                  {`${manualMatches.length} manual claim link${
                    manualMatches.length === 1 ? "" : "s"
                  } between renewal and previous period applied for delta calculations.`}
                </>
              )}
            </Alert>
            {manualMatches.length > 0 && acceptedMatches.length > 0 && (
              <Menu
                anchorEl={editMenuAnchor}
                open={Boolean(editMenuAnchor)}
                onClose={() => setEditMenuAnchor(null)}
              >
                <MenuItem
                  onClick={() => {
                    setEditMenuAnchor(null);
                    handleOpenManualLinkerFromBanner();
                  }}
                >
                  {"Edit manual links"}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setEditMenuAnchor(null);
                    handleEditMatches();
                  }}
                >
                  {"Edit AI matches"}
                </MenuItem>
              </Menu>
            )}
          </>
        )}
        {aiClaimsMatchEnabled &&
          acceptedMatches.length === 0 &&
          storedMatchingResults &&
          storedMatchingResults.results?.length > 0 && (
            <Alert
              severity={"warning"}
              action={
                <Button
                  variant={"outlined"}
                  color={"warning"}
                  size={"small"}
                  onClick={handleEditMatches}
                  disableElevation
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    bgcolor: theme.brand.white,
                    borderColor: "warning.main",
                    color: "warning.dark",
                    "&:hover": {
                      bgcolor: "warning.light",
                      borderColor: "warning.dark",
                    },
                  }}
                >
                  {"Review"}
                </Button>
              }
            >
              {storedMatchingResults?.results?.length || 0}
              {" potential claim match"}
              {storedMatchingResults?.results?.length === 1 ? "" : "es"}
              {" detected between renewal and previous period. Click "}
              <i>{"Review"}</i>
              {" to examine and apply matches."}
            </Alert>
          )}
        <Stack>
          <Typography variant={"subtitle1"}>{"Current"}</Typography>
          <WithLoading isLoading={isAllClaimsChartFetching}>
            <FilteredClaimsChart
              data={allClaimsChart}
              dateDomain={dateDomain}
              numberTicks={numberTicks}
              numberDomain={numberDomain}
              filter={{ attachment: deltaThreshold }}
              zoomIdentifier={zoomIdentifier}
              disabled
              hideBars={allClaimsChart?.attachmentOverride != null}
              Overlay={
                allClaimsChart?.attachmentOverride != null
                  ? TooManyClaimsOverlay
                  : null
              }
              OverlayProps={{
                override: Math.max(
                  allClaimsChart?.attachmentOverride ?? 0,
                  priorAllClaimsChart?.attachmentOverride ?? 0
                ),
                onApplyOverride: (threshold) => {
                  dispatch(claimsActions.updateDeltaClaimThreshold(threshold));
                },
              }}
            />
          </WithLoading>
          <Typography variant={"subtitle1"}>{"Prior"}</Typography>
          <WithLoading isLoading={isPriorAllClaimsChartFetching}>
            <FilteredClaimsChart
              data={priorAllClaimsChart}
              dateDomain={dateDomain}
              numberTicks={numberTicks}
              numberDomain={numberDomain}
              filter={{ attachment: deltaThreshold }}
              zoomIdentifier={zoomIdentifier}
              disabled
              hideBars={priorAllClaimsChart?.attachmentOverride != null}
              Overlay={
                priorAllClaimsChart?.attachmentOverride != null
                  ? TooManyClaimsOverlay
                  : null
              }
              OverlayProps={{
                override: Math.max(
                  allClaimsChart?.attachmentOverride ?? 0,
                  priorAllClaimsChart?.attachmentOverride ?? 0
                ),
                onApplyOverride: (threshold) => {
                  dispatch(claimsActions.updateDeltaClaimThreshold(threshold));
                },
              }}
            />
          </WithLoading>
          <Typography variant={"subtitle1"}>
            {query?.limit ? `Delta (Top ${query.limit})` : "Delta"}
          </Typography>
          <WithLoading isLoading={isFetching}>
            <WithError
              error={
                isError
                  ? `Error running claims comparison. Please contact ${config.SUPPORT_EMAIL}.`
                  : null
              }
            >
              <ResponsiveContainer width={"100%"} height={600}>
                <BarChart
                  key={`delta-chart-${manualMatches.length}-${acceptedMatches.length}`}
                  ref={chartRef}
                  height={600}
                  data={includedDeltas}
                  onMouseDown={(e) => {
                    const monthOfEvent = getMonthOfEvent(e);
                    if (monthOfEvent != null) {
                      setZoom({
                        from: monthOfEvent,
                      });
                    }
                  }}
                  onMouseMove={(e) => {
                    const monthOfEvent = getMonthOfEvent(e);
                    if (monthOfEvent != null && zoom?.from != null) {
                      setZoom({
                        from: zoom.from,
                        to: monthOfEvent,
                        selecting: true,
                      });
                    }
                  }}
                  onMouseUp={() => {
                    if (zoom?.from != null && zoom?.to != null) {
                      const [from, to] = [
                        Math.min(zoom.from, zoom.to),
                        Math.max(zoom.from, zoom.to),
                      ];
                      dispatch(
                        claimsActions.updateChartZoom(zoomIdentifier, {
                          from,
                          to,
                        })
                      );
                    }
                    setZoom({});
                  }}
                  isAnimationActive={false}
                >
                  <CartesianGrid strokeDasharray={"3 3"} id={cartesianGridId} />
                  <Tooltip
                    formatter={tooltipFormatter}
                    labelFormatter={tooltipLabelFormatter}
                    content={<CustomTooltipContent />}
                    position={tooltipPosition}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey={"delta"}
                    barSize={5}
                    shape={charts.minWidthBarFactory(5)}
                    isAnimationActive={false}
                    isUpdateAnimationActive={false}
                  >
                    {includedDeltas.map((comparison, index) => (
                      <Cell
                        key={`${comparison.months}-${comparison.delta}-${index}`}
                        fill={
                          comparison.delta < 0
                            ? theme.brand.red[600]
                            : theme.brand.green[200]
                        }
                      />
                    ))}
                  </Bar>
                  {zoom?.from != null && zoom?.to != null && (
                    <ReferenceArea
                      x1={zoom.from}
                      x2={zoom.to}
                      ifOverflow={"hidden"}
                      fillOpacity={0.25}
                    />
                  )}
                  <XAxis
                    dataKey={"months"}
                    type={"number"}
                    tickFormatter={charts.dateTickFormatter}
                    ticks={charts.getDateTicks(zoomedDomain)}
                    domain={zoomedDomain}
                    axisLine={false}
                    style={zoom?.selecting ? { userSelect: "none" } : {}}
                  />
                  {/* Draw the x-axis line at y=0. */}
                  <ReferenceLine y={0} stroke={theme.brand.black} />
                  <YAxis
                    domain={doubleNumberDomain}
                    ticks={doubleNumberTicks}
                    tickFormatter={chartUtils.tickFormatter}
                    style={zoom?.selecting ? { userSelect: "none" } : {}}
                  />
                </BarChart>
              </ResponsiveContainer>
            </WithError>
          </WithLoading>
        </Stack>
        <Box>
          <ClaimsSlider
            title={"Attachment"}
            value={deltaThreshold}
            onChangeNumber={(value) =>
              dispatch(claimsActions.updateDeltaClaimThreshold(value))
            }
            min={0}
            max={biggestClaim || 1e9}
          />
        </Box>
        {chartZoom && (
          <Stack direction={"row"} justifyContent={"right"}>
            <Button
              disableElevation
              variant={"contained"}
              color={"secondary"}
              onClick={() => {
                dispatch(claimsActions.clearChartZoom(zoomIdentifier));
              }}
            >
              {"Clear Zoom"}
            </Button>
          </Stack>
        )}
      </Stack>
    </Component>
  );
};

export default ClaimsAndDeltaChart;
